import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loading, EmptyState } from "@/components/common";
import { fmtMeetingDate, MEETING_TYPE_LABELS } from "@/lib/meetingsApi";
import { CONSIDERATION_STATE } from "@/lib/forumApi";
import { ArrowLeft, CalendarDays, MapPin, Radio, ChevronRight, CheckCircle2, Circle, Landmark } from "lucide-react";

export default function ForumMeeting() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  const load = useCallback(() => {
    api.get(`/forum/meetings/${id}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [id]);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  if (err) return <EmptyState icon={Landmark} title="Meeting not available" description="You may not have access, or it has not been published." />;
  if (!data) return <Loading label="Loading meeting…" />;
  const m = data.meeting;
  const live = m.status === "in_progress";
  const currentItem = data.agenda.find((a) => a.is_current);

  return (
    <div data-testid="forum-meeting" className="space-y-6">
      <Link to="/forum" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-brand-navy">
        <ArrowLeft className="h-4 w-4" /> My Forum
      </Link>

      <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-card">
        {live && <span className="inline-flex items-center gap-1 rounded-full bg-brand-gold px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-[#5c4405] mb-2"><Radio className="h-3 w-3" /> Live Meeting</span>}
        <div className="text-xs font-semibold uppercase tracking-wider text-brand-civic">{m.governing_body_name}</div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-brand-ink mt-1">{m.title}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-slate-500">
          <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> {fmtMeetingDate(m.date, m.start_time)}</span>
          {m.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {m.location}</span>}
          <span>{MEETING_TYPE_LABELS[m.meeting_type] || m.meeting_type}</span>
        </div>
        {data.quorum && (
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm border-t border-brand-line pt-3">
            {data.my_attendance?.attendance_status && (
              <span data-testid="forum-my-attendance" className="inline-flex items-center gap-1.5 font-semibold text-brand-ink capitalize">
                <CheckCircle2 className="h-4 w-4 text-brand-teal" /> You are {data.my_attendance.attendance_status}
              </span>
            )}
            <span data-testid="forum-quorum" className={`inline-flex items-center gap-1.5 font-semibold ${data.quorum.met ? "text-brand-teal" : "text-red-600"}`}>
              {data.quorum.met ? <CheckCircle2 className="h-4 w-4" /> : <Radio className="h-4 w-4" />}
              Quorum {data.quorum.met ? "Established" : "Not Established"}
              <span className="text-slate-400 font-normal">· {data.quorum.present_voting} of {data.quorum.eligible_voting} present · {data.quorum.required} required</span>
            </span>
          </div>
        )}
      </div>

      {currentItem && (
        <Link to={`/forum/meetings/${id}/items/${currentItem.item_id}`} data-testid="forum-current-item-banner"
          className="block rounded-2xl border border-brand-gold bg-brand-gold-50 p-5 hover:shadow-elevated transition-all">
          <div className="text-xs font-bold uppercase tracking-wider text-[#9A7212] mb-1">Current Item</div>
          <div className="flex items-center justify-between gap-3">
            <div>
              <span className="font-display text-lg font-extrabold text-brand-ink">{currentItem.number} · {currentItem.title}</span>
              <div className="mt-1"><StateChip state={currentItem.consideration?.status} /></div>
            </div>
            <span className="inline-flex items-center gap-1 rounded-lg bg-brand-navy px-4 py-2 text-sm font-bold text-white shrink-0">Open <ChevronRight className="h-4 w-4" /></span>
          </div>
        </Link>
      )}

      <section>
        <h2 className="text-sm font-bold uppercase tracking-wider text-brand-civic mb-3">Agenda</h2>
        <div className="space-y-2" data-testid="forum-agenda">
          {data.agenda.map((it) => {
            const st = it.consideration?.status;
            const done = st === "completed" || st === "voting_closed";
            return (
              <Link key={it.item_id || it.number} to={it.item_id ? `/forum/meetings/${id}/items/${it.item_id}` : "#"}
                data-testid="forum-agenda-item"
                className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 transition-colors ${it.is_current ? "border-brand-gold bg-brand-gold-50" : "border-brand-line bg-white hover:border-brand-civic/40"}`}>
                {done ? <CheckCircle2 className="h-5 w-5 text-brand-teal shrink-0" /> : it.is_current ? <Radio className="h-5 w-5 text-brand-gold shrink-0" /> : <Circle className="h-5 w-5 text-slate-300 shrink-0" />}
                <span className="text-sm font-bold text-brand-navy w-10 shrink-0">{it.number}</span>
                <span className="flex-1 text-brand-ink font-medium truncate">{it.title}</span>
                {it.consideration?.official_result_label
                  ? <span className="inline-flex rounded-full border border-brand-teal/30 bg-brand-teal-50 px-2.5 py-0.5 text-xs font-semibold text-brand-teal">{it.consideration.official_result_label}</span>
                  : <StateChip state={st} />}
                <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function StateChip({ state }) {
  if (!state || state === "scheduled") return null;
  const s = CONSIDERATION_STATE[state] || { label: state, cls: "bg-slate-100 text-slate-600 border-slate-300" };
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${s.cls}`}>{s.label}</span>;
}
